if (!customElements.get('collection-list-grid')) {
  class CollectionListGrid extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('.clg-grid');
      this.prevBtn = this.querySelector('.clg-arrow--prev');
      this.nextBtn = this.querySelector('.clg-arrow--next');
      this.dotsWrap = this.querySelector('.clg-dots');
      this.items = this.grid ? Array.from(this.grid.children) : [];

      if (!this.grid || this.items.length === 0) return;

      this.sliderDesktop = this.dataset.sliderDesktop === 'true';
      this.sliderMobile = this.dataset.sliderMobile === 'true';
      this.showArrows = this.dataset.showArrows === 'true';
      this.showDots = this.dataset.showDots === 'true';

      if (!this.showArrows) {
        if (this.prevBtn) this.prevBtn.style.display = 'none';
        if (this.nextBtn) this.nextBtn.style.display = 'none';
      }

      this.desktopQuery = window.matchMedia('(min-width: 990px)');

      this.boundEvaluate = this.evaluateMode.bind(this);
      this.boundOnScroll = this.debounce(this.updateActiveState.bind(this), 80);

      this.grid.addEventListener('scroll', this.boundOnScroll, { passive: true });

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.scrollByPage(-1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.scrollByPage(1));
      }

      if (typeof this.desktopQuery.addEventListener === 'function') {
        this.desktopQuery.addEventListener('change', this.boundEvaluate);
      } else if (typeof this.desktopQuery.addListener === 'function') {
        this.desktopQuery.addListener(this.boundEvaluate);
      }

      window.addEventListener('resize', this.debounce(this.boundEvaluate, 150));

      this.evaluateMode();
    }

    disconnectedCallback() {
      if (this.grid) this.grid.removeEventListener('scroll', this.boundOnScroll);
      window.removeEventListener('resize', this.boundEvaluate);
    }

    debounce(fn, wait) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
      };
    }

    isDesktop() {
      return this.desktopQuery.matches;
    }

    isSliderActive() {
      return this.isDesktop() ? this.sliderDesktop : this.sliderMobile;
    }

    evaluateMode() {
      const active = this.isSliderActive();
      this.grid.classList.toggle('is-slider', active);
      this.setAttribute('data-is-slider-active', active ? 'true' : 'false');

      if (active) {
        this.buildDots();
        this.updateActiveState();
      } else if (this.dotsWrap) {
        this.dotsWrap.innerHTML = '';
      }
    }

    getVisibleCount() {
      if (!this.items.length) return 1;
      const containerWidth = this.grid.clientWidth;
      const itemWidth = this.items[0].getBoundingClientRect().width;
      if (!itemWidth) return 1;
      return Math.max(1, Math.round(containerWidth / itemWidth));
    }

    getPageCount() {
      const visible = this.getVisibleCount();
      return Math.max(1, Math.ceil(this.items.length / visible));
    }

    buildDots() {
      if (!this.dotsWrap || !this.showDots) {
        if (this.dotsWrap) this.dotsWrap.innerHTML = '';
        return;
      }

      const pageCount = this.getPageCount();
      if (this.dotsWrap.childElementCount === pageCount) return;

      this.dotsWrap.innerHTML = '';
      for (let i = 0; i < pageCount; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'clg-dot';
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.addEventListener('click', () => this.scrollToPage(i));
        this.dotsWrap.appendChild(dot);
      }
    }

    scrollToPage(pageIndex) {
      const visible = this.getVisibleCount();
      const targetItem = this.items[pageIndex * visible];
      if (targetItem) {
        this.grid.scrollTo({ left: targetItem.offsetLeft, behavior: 'smooth' });
      }
    }

    scrollByPage(direction) {
      const visible = this.getVisibleCount();
      const itemWidth = this.items[0].getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(this.grid).columnGap || '0') || 0;
      const amount = (itemWidth + gap) * visible;
      this.grid.scrollBy({ left: amount * direction, behavior: 'smooth' });
    }

    updateActiveState() {
      if (!this.grid) return;

      const scrollLeft = this.grid.scrollLeft;

      if (this.showDots && this.dotsWrap && this.dotsWrap.childElementCount) {
        const visible = this.getVisibleCount();
        let closestIndex = 0;
        let closestDistance = Infinity;

        this.items.forEach((item, index) => {
          const distance = Math.abs(item.offsetLeft - scrollLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        const activePage = Math.floor(closestIndex / visible);
        Array.from(this.dotsWrap.children).forEach((dot, i) => {
          dot.classList.toggle('is-active', i === activePage);
        });
      }

      if (this.showArrows) {
        const maxScroll = this.grid.scrollWidth - this.grid.clientWidth - 1;
        if (this.prevBtn) this.prevBtn.disabled = scrollLeft <= 0;
        if (this.nextBtn) this.nextBtn.disabled = scrollLeft >= maxScroll;
      }
    }
  }

  customElements.define('collection-list-grid', CollectionListGrid);
}